import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Link, mergeStyleSets, MessageBar } from '@fluentui/react';
import { AppReviewItemToolbar } from '@shared/components/appReviewItemToolbar';
import { ReviewIsvReply } from '@shared/components/reviewIsvReply';
import { AppReviewCommentCollection } from '@shared/containers/appReviewCommentCollection';
import { launchTelemetry } from '@shared/utils/reviewsUtils';
import { Constants } from '@shared/utils/constants';
import { logger } from '@src/logger';
import { useBoolean } from '@fluentui/react-hooks';
import * as PropTypes from 'prop-types';
const contentStyles = mergeStyleSets({
    markAsHelpfulAuthMsg: {
        marginTop: 24
    }
});
const MARK_AS_HELPFUL_MESSAGE_HIDE_MS = 2000;
export const AppReviewSocialContent = ({ isEmbed, review, onReportClick, locale, iconUrl, publisherName, isSignedIn, isMarkedAsHelpful, onToggleMarkAsHelpful, openSignInModal }, context)=>{
    const [commentsExpanded, setCommentsExpanded] = useState(false);
    const [markAsHelpfulAuthMsgExpanded, setMarkAsHelpfulAuthMsgExpanded] = useState(false);
    const [showMarkedAsHelpfulThanks, { setFalse: hideMarkAsHelpfulMessage, setTrue: showMarkAsHelpfulMessage }] = useBoolean(false);
    const triggerMarkAsHelpfulThanksMessage = ()=>{
        showMarkAsHelpfulMessage();
        // Hide the "Thank you" callout after the configured amount of milliseconds
        const timeout = setTimeout(()=>{
            hideMarkAsHelpfulMessage();
            clearTimeout(timeout);
        }, MARK_AS_HELPFUL_MESSAGE_HIDE_MS);
    };
    useEffect(()=>{
        if (markAsHelpfulAuthMsgExpanded && isSignedIn) {
            setMarkAsHelpfulAuthMsgExpanded(false);
        }
    }, [
        isSignedIn
    ]);
    const toggleComments = ()=>{
        const payload = {
            action: commentsExpanded ? Constants.Telemetry.Action.Collapse : Constants.Telemetry.Action.Expand,
            actionModifier: Constants.Telemetry.ActionModifier.ReviewItemCommentsToggleButton,
            details: {
                reviewId: review === null || review === void 0 ? void 0 : review.id,
                appId: review === null || review === void 0 ? void 0 : review.offer_id
            }
        };
        launchTelemetry(payload.action, payload.actionModifier, payload.details);
        logger.info(JSON.stringify(payload.details), {
            action: payload.action,
            actionModifier: payload.actionModifier
        });
        setMarkAsHelpfulAuthMsgExpanded(false);
        setCommentsExpanded((prevCommentsExpanded)=>!prevCommentsExpanded);
    };
    const toggleMarkAsHelpfulAuthMsg = ()=>{
        const payload = {
            action: markAsHelpfulAuthMsgExpanded ? Constants.Telemetry.Action.Collapse : Constants.Telemetry.Action.Expand,
            actionModifier: Constants.Telemetry.ActionModifier.ReviewItemMarkAsHelpfulUnauthorizedMessage,
            details: {
                reviewId: review === null || review === void 0 ? void 0 : review.id,
                appId: review === null || review === void 0 ? void 0 : review.offer_id
            }
        };
        launchTelemetry(payload.action, payload.actionModifier, payload.details);
        logger.info(JSON.stringify(payload.details), {
            action: payload.action,
            actionModifier: payload.actionModifier
        });
        setCommentsExpanded(false);
        setMarkAsHelpfulAuthMsgExpanded((prevMarkAsHelpfulAuthMsgExpanded)=>!prevMarkAsHelpfulAuthMsgExpanded);
    };
    const onMarkAsHelpfulClick = ()=>{
        const { id: reviewId, offer_id: offerId } = review;
        const telemetryPayload = {
            reviewId,
            offerId,
            prevIsMarkedAsHelpful: isMarkedAsHelpful
        };
        launchTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.ReviewItemMarkAsHelpfulButton, telemetryPayload);
        logger.info(JSON.stringify(telemetryPayload), {
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.ReviewItemMarkAsHelpfulButton
        });
        if (isSignedIn) {
            const isHelpful = !isMarkedAsHelpful;
            onToggleMarkAsHelpful({
                isHelpful
            });
            if (isHelpful) {
                triggerMarkAsHelpfulThanksMessage();
            }
        } else {
            toggleMarkAsHelpfulAuthMsg();
        }
    };
    const onMarkAsHelpfulSignInClick = ()=>{
        openSignInModal();
        launchTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.ReviewItemMarkAsHelpfulSignIn, {
            reviewId: review === null || review === void 0 ? void 0 : review.id,
            offerId: review === null || review === void 0 ? void 0 : review.offer_id
        });
        logger.info(JSON.stringify({
            reviewId: review === null || review === void 0 ? void 0 : review.id,
            offerId: review === null || review === void 0 ? void 0 : review.offer_id
        }), {
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.ReviewItemMarkAsHelpfulSignIn
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isEmbed || !review.title && !review.content ? null : /*#__PURE__*/ _jsx(AppReviewItemToolbar, {
                onReportAbuse: onReportClick,
                onToggleComments: toggleComments,
                onMarkAsHelpful: onMarkAsHelpfulClick,
                review: review,
                isMarkedAsHelpful: isMarkedAsHelpful,
                showMarkAsHelpfulThanks: showMarkedAsHelpfulThanks
            }),
            review.isv_reply && /*#__PURE__*/ _jsx(ReviewIsvReply, {
                isvReply: review.isv_reply,
                locale: locale,
                iconUrl: iconUrl,
                publisherName: publisherName
            }),
            /*#__PURE__*/ _jsx(AppReviewCommentCollection, {
                expanded: commentsExpanded,
                review: review,
                locale: locale
            }),
            markAsHelpfulAuthMsgExpanded && /*#__PURE__*/ _jsxs(MessageBar, {
                styles: {
                    root: contentStyles.markAsHelpfulAuthMsg
                },
                children: [
                    context.loc('ReviewItem_MarkAsHelpful_Unauthenticated_SignIn_Description', 'To mark as helpful please '),
                    /*#__PURE__*/ _jsx(Link, {
                        onClick: onMarkAsHelpfulSignInClick,
                        children: context.loc('ReviewItem_MarkAsHelpful_Unauthenticated_SignIn_Link', 'sign in.')
                    })
                ]
            })
        ]
    });
};
AppReviewSocialContent.contextTypes = {
    loc: PropTypes.func
};
